import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareMapPaths = (p1, p2) => p1.id === p2.id;

const mapPathSlice = createSlice({
    name: 'mapPaths',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload
        },
        addOrUpdate: (state, action) => {
            for (const p of ensureArray(action.payload)) {
                let idx = state.data.findIndex(p2 => compareMapPaths(p, p2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, p);
                } else {
                    state.data.push(p);
                }
            }
        },
        remove: (state, action) => {
            for (const p of ensureArray(action.payload)) {
                let idx = state.data.findIndex(p2 => compareMapPaths(p2, p));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => mapPathSlice.getInitialState());
    }
});

// #region network operations
const fetchMapPaths = network => network.get('/api/v1/mappaths', mapPathSlice.actions.set);
export const updateMapPath = (network, delta) => network.put('/api/v1/mappaths', delta, mapPathSlice.actions.addOrUpdate);
export const createMapPath = (network, path) => network.post('/api/v1/mappaths', path, mapPathSlice.actions.addOrUpdate);
export const removeMapPath = (network, id) => network.delete('/api/v1/mappath/' + encodeURIComponent(id), mapPathSlice.actions.remove);
// #endregion

// #region selectors
const selectMapPaths = state => state.mapPaths.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectMapPathByID = createSelector([selectMapPaths, (mapPaths, id) => id], (mapPaths, id) => mapPaths.find(z => z.id === id));
const selectMapPath = id => state => selectMapPathByID(state, id);

// #endregion

// #region listeners
const listen = {
    mapPathCreated: (mapPath, { dispatch }) => dispatch(mapPathSlice.actions.addOrUpdate(mapPath)),
    mapPathUpdated: (mapPath, { dispatch }) => dispatch(mapPathSlice.actions.addOrUpdate(mapPath)),
    mapPathRemoved: (mapPath, { dispatch }) => dispatch(mapPathSlice.actions.remove(mapPath)),
}
// #endregion

// #region permission checks
export const canInsertPath = (credentials, path) => credentials.access.hasFull('administrator');
export const canUpdatePath = canInsertPath;
export const canRemovePath = canInsertPath;
// #endregion

export const [useMapPaths] = dataHookFactory(selectMapPaths, { run: fetchMapPaths, listen });
export const [useMapPath] = dataHookFactory(selectMapPath, initMapFactory(id => fetchMapPaths, listen));

const reducerDetails = { name: mapPathSlice.name, reducer: mapPathSlice.reducer };
export default reducerDetails;